import React, { MouseEventHandler, useMemo } from 'react';
import { injectIntl, IntlShape } from 'react-intl';
import { toast } from 'react-toastify';
import hash from 'object-hash';
import { get } from 'lodash';
import { translate } from 'containers/services/translate';
import messages from 'containers/CourtManagerLegalProceedings/JudgementAdd/messages';
import { PROCEDURE_STATUSES, PROCEDURE_STEPS } from 'containers/data-structure';
import {
  IJudgementAdd,
  JudgementOnMeritsType,
} from 'containers/LawyerLegalProceedingsPage/typings';
import { PROCEDURE_TYPES } from 'containers/LawyerLegalProceedingsPage/constants';
import { JudgementADDDocumentType } from 'containers/common/LegalProceedings/JudgementAdd/Forms/JudgementAddPrononcings/typings';
import currentUserStorage from 'containers/services/security/current-user-storage';
import { ExpertReportType } from 'containers/common/LegalProceedings/ExpertReport/typings';
import { showAlert } from 'containers/CourtManagerLegalProceedings/JudgementAdd/Dialogs/utils';
import * as featureService from 'features/fflip-helpers';
import { FEATURES } from 'features/features';
import SahamDialog from 'components/SahamDialog';
import { validateAfterRejection } from 'containers/common/LegalProceedings/JudgementAdd/Recap/Pronouncing/utils';

export interface PronouncingValidationDialogProps {
  intl: IntlShape;
  open: boolean;
  handleClose: Function;
  lawyer: string;
  claimId: number;
  judgementAdd?: IJudgementAdd | {};
  judgementAddDocument: JudgementADDDocumentType;
  requestMakeJudgementAddDocumentDecision?: Function;
  claimNumber?: string;
  setJudgementAdd?: Function;
  requestGetJudgementAdd?: Function;
  expertReport?: ExpertReportType;
  judgementOnMerits?: JudgementOnMeritsType;
  toUpdate: boolean;
  setOpenFeeNoteDialog: Function;
}

export const PronouncingValidationDialog: React.FC<PronouncingValidationDialogProps> = (
  props,
): JSX.Element => {
  const findAddDocumentStore = get(
    props.judgementAdd,
    'judgementAddDocuments',
    [],
  ).find((el: any) => el.id === get(props.judgementAddDocument, 'id'));

  const { hasExpertise, hasJom } = useMemo(
    () =>
      showAlert(
        props.judgementAddDocument,
        props.expertReport,
        props.judgementOnMerits,
      ),
    [props.judgementAddDocument, props.expertReport, props.judgementOnMerits],
  );

  const handleValidate = () => {
    let payload: any;

    if (hash(findAddDocumentStore) === hash(props.judgementAddDocument)) {
      payload = {
        step: PROCEDURE_STEPS.JUDGEMENT_ADD,
        status: props.toUpdate
          ? get(props.judgementAddDocument, 'status')
          : PROCEDURE_STATUSES.VALIDATED,

        id: get(props.judgementAddDocument, 'id'),
      };
    } else {
      payload = {
        ...props.judgementAddDocument,
        status: props.toUpdate
          ? get(props.judgementAddDocument, 'status')
          : PROCEDURE_STATUSES.MODIFIED,
      };
    }
    const judgementAddDocuments = get(
      props.judgementAdd,
      'judgementAddDocuments',
      [],
    ).map((jadd: JudgementADDDocumentType) => {
      return jadd.id === props.judgementAddDocument.id
        ? { ...jadd, status: payload.status }
        : jadd;
    });
    props.setJudgementAdd &&
      props.setJudgementAdd({
        ...props.judgementAdd,
        judgementAddDocuments,
      });
    props.requestMakeJudgementAddDocumentDecision &&
      props.requestMakeJudgementAddDocumentDecision(
        {
          ...payload,
          claimId: props.claimId,
          procedureType: PROCEDURE_TYPES.FIRST_INSTANCE,
          judiciaryProcessId: get(props.judgementAdd, 'judiciaryProcessId'),
          username: currentUserStorage.get().username,
          claimNumber: props.claimNumber,
          toUpdate: props.toUpdate,
          isInboxesActive: featureService.hasFeature(
            FEATURES.ATTRIBUTION_INBOXES_MANAGER,
          ),
          isTrackingActive: featureService.hasFeature(
            FEATURES.TRACKING_ACTIONS_USER,
          ),
        },
        handleSuccessCallback,
      );
  };
  const handleSuccessCallback = () => {
    props.toUpdate
      ? toast.success(
          translate(
            messages.courtManagerPronouncingUpdateDialogSuccessMessage,
            props,
          ),
        )
      : toast.success(
          translate(
            messages.courtManagerValidationDialogSuccessMessage,
            props,
            {
              lawyer: props.lawyer,
            },
          ),
        );
    props.requestGetJudgementAdd &&
      props.requestGetJudgementAdd(
        props.claimId,
        PROCEDURE_TYPES.FIRST_INSTANCE,
        get(props.judgementAdd, 'judiciaryProcessId'),
      );
    !props.toUpdate &&
      featureService.hasFeature(FEATURES.EVOLUTIONS_FEE_NOTE) &&
      props.setOpenFeeNoteDialog(true);
    props.handleClose();
  };

  const title =
    hasJom && hasExpertise
      ? translate(messages.courtManagerValidationTitle, props, {
          type: translate(messages.have, props),
          judiciaryStep: `${translate(
            messages.expertReport,
            props,
          )} et le ${translate(messages.jom, props)}`,
          plural: 's',
        })
      : hasJom && !hasExpertise
      ? translate(messages.courtManagerValidationTitle, props, {
          type: translate(messages.has, props),
          judiciaryStep: translate(messages.jom, props),
          plural: '',
        })
      : !hasJom && hasExpertise
      ? translate(messages.courtManagerValidationTitle, props, {
          type: translate(messages.has, props),
          judiciaryStep: translate(messages.expertReport, props),
          plural: '',
        })
      : translate(messages.courtManagerUpdateDialogContent, props);

  return (
    <SahamDialog
      handleValidate={handleValidate}
      handleClose={props.handleClose as MouseEventHandler}
      title={translate(messages.courtManagerConfirmationDialogTitle, props)}
      cancelTitle={translate(
        messages.courtManagerConfirmationDialogCancel,
        props,
      )}
      confirmTitle={translate(
        messages.courtManagerConfirmationDialogConfirm,
        props,
      )}
      open={props.open}
    >
      {validateAfterRejection(props.judgementAddDocument)
        ? translate(messages.courtManagerConfirmationOnRejectionTitle, props)
        : featureService.hasFeature(FEATURES.MANAGE_CLAIM_BY_MANAGER)
        ? title
        : translate(messages.courtManagerConfirmationDialogContent, props)}
    </SahamDialog>
  );
};

export default injectIntl(PronouncingValidationDialog);
